import { apiService } from '@shared/services/apiServices';
import {
  ContactRequestErrorCode,
  contactRequestErrorMessages,
} from '../errors';
import type {
  EligibleAssignee,
  ListEligibleAssigneesParams,
  ListEligibleAssigneesResult,
} from '../types/assignContactRequestTypes';
import contactRequestEndpoints from './contactRequestEndpoints';

/** Backend default page size for the eligible-assignees endpoint (max 100). */
export const ELIGIBLE_ASSIGNEES_PAGE_LIMIT = 10;

interface EligibleAssigneesResponse {
  data?: Array<{
    id: string;
    user_id: string;
    name: string;
    email?: string;
  }>;
  pagination?: {
    hasNextPage?: boolean;
  };
}

const errorFor = (code: ContactRequestErrorCode): string =>
  contactRequestErrorMessages[code];

/**
 * Fetches one page of admins eligible to be assigned to a contact request from
 * the dedicated endpoint
 * (`GET .../contact-requests/{id}/eligible-assignees`). The response is the
 * paged `{ data, pagination }` shape; `hasNextPage` is read straight from
 * `pagination`. Callers page through results (see `useAssignContactRequest`)
 * rather than fetching everything at once.
 *
 * Access: SUPER_ADMIN | ADMIN
 */
export const listEligibleAssignees = async ({
  contactRequestId,
  page = 1,
  limit = ELIGIBLE_ASSIGNEES_PAGE_LIMIT,
  search,
}: ListEligibleAssigneesParams): Promise<ListEligibleAssigneesResult> => {
  const trimmedId = contactRequestId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: errorFor(ContactRequestErrorCode.UNEXPECTED_ERROR),
    };
  }

  const queryParams = new URLSearchParams();
  queryParams.append('page', String(page));
  queryParams.append('limit', String(limit));
  if (search?.trim()) {
    queryParams.append('search', search.trim());
  }

  const endpoint = `${contactRequestEndpoints.ELIGIBLE_ASSIGNEES(
    trimmedId
  )}?${queryParams.toString()}`;

  const response = await apiService.getAPI(endpoint, {
    skipGlobalErrorRedirect: true,
  });

  if (response.error) {
    if (response.isNetworkError) {
      return {
        success: false,
        error: errorFor(ContactRequestErrorCode.NETWORK_ERROR),
      };
    }
    if (response.status === 401 || response.status === 403) {
      return {
        success: false,
        error: errorFor(ContactRequestErrorCode.FORBIDDEN),
      };
    }
    if (response.status === 404) {
      return {
        success: false,
        error: errorFor(ContactRequestErrorCode.NOT_FOUND),
      };
    }
    return {
      success: false,
      error: errorFor(ContactRequestErrorCode.UNEXPECTED_ERROR),
    };
  }

  const payload = (response.data ?? {}) as EligibleAssigneesResponse;
  const data: EligibleAssignee[] = (payload.data ?? []).map((assignee) => ({
    id: assignee.id,
    user_id: assignee.user_id,
    name: assignee.name,
    ...(assignee.email ? { email: assignee.email } : {}),
  }));

  return {
    success: true,
    data,
    hasNextPage: payload.pagination?.hasNextPage ?? false,
  };
};
